import * as React from "react";
import SideNavigation from "@amzn/awsui-components-react/polaris/side-navigation";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Flashbar, { FlashbarProps } from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import PageShell from "../../../../global/components/PageShell";
import ProfileLookupSection from "./ProfileLookupSection";
import DrugSelector from "./DrugSelector";
import OverridesTable, { UcsOverride } from "./OverridesTable";
import CreateOverrideForm, { OverrideFormData, EMPTY_FORM_DATA } from "./CreateOverrideForm";
import ApprovalResponseSection, { ApprovalResponse } from "./ApprovalResponseSection";
import ExpireOverridesSection from "./ExpireOverridesSection";
import PlaceOrderSection from "./PlaceOrderSection";
import ConsumptionEstimatesSection from "./ConsumptionEstimatesSection";
import { ApprovalStatus, ApprovalReason, RequesterRole, APPROVAL_TO_OVERRIDE_REASON } from "../../../consts/ApprovalsAndOverrides";
import { API } from "aws-amplify";

export interface DrugOption {
  label: string;
  value: string;
  drugId: string;
  asin: string;
  rxnormName: string;
}

export interface PrescriptionOption {
  prescriptionId: string;
  daysSupply: number;
  drugAsin: string;
  orderId: string;
}

type PageView = "create-override" | "expire-overrides" | "place-order" | "consumption-estimates";

interface State {
  navOpen: boolean;
  activePage: PageView;
  profileId: string;
  isLoadingPrescriptions: boolean;
  drugs: DrugOption[];
  prescriptions: PrescriptionOption[];
  selectedDrug: DrugOption | null;
  overrides: UcsOverride[];
  isLoadingOverrides: boolean;
  formData: OverrideFormData;
  isSubmitting: boolean;
  approvalResponse: ApprovalResponse | null;
  isOverridingApproval: boolean;
  overrideApprovalCompleted: boolean;
  isCreatingOverride: boolean;
  flashMessages: FlashbarProps.MessageDefinition[];
}

class ConsumptionGuardOverridePage extends React.Component<{}, State> {
  private approvalResponseRef = React.createRef<HTMLDivElement>();
  private flashbarRef = React.createRef<HTMLDivElement>();

  constructor(props: {}) {
    super(props);
    this.state = {
      navOpen: true,
      activePage: "create-override",
      profileId: "",
      isLoadingPrescriptions: false,
      drugs: [],
      prescriptions: [],
      selectedDrug: null,
      overrides: [],
      isLoadingOverrides: false,
      formData: EMPTY_FORM_DATA,
      isSubmitting: false,
      approvalResponse: null,
      isOverridingApproval: false,
      overrideApprovalCompleted: false,
      isCreatingOverride: false,
      flashMessages: [],
    };
  }

  private scrollToFlashbar = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  private handleLookup = async () => {
    const { profileId } = this.state;
    this.setState({ isLoadingPrescriptions: true, drugs: [], prescriptions: [], selectedDrug: null, overrides: [], approvalResponse: null, overrideApprovalCompleted: false, flashMessages: [] });

    try {
      const response = await API.post("ApaqPortalAPI", "/prescription/profile/search", {
        body: { profileId },
      });

      const rawPrescriptions: string[] = response.prescriptions || [];
      const parsed = rawPrescriptions.map((item: any) => {
        if (typeof item === "string") {
          try { return JSON.parse(item); } catch { return null; }
        }
        return item;
      }).filter(Boolean);

      const drugMap = new Map<string, DrugOption>();
      for (const rx of parsed) {
        const drug = rx.drug;
        if (drug && drug.asin) {
          if (!drugMap.has(drug.asin)) {
            drugMap.set(drug.asin, {
              label: `${drug.rxnormName || drug.pillPackName || "Unknown"} (${drug.id || "?"} - ${drug.asin})`,
              value: drug.asin,
              drugId: drug.id || "",
              asin: drug.asin,
              rxnormName: drug.rxnormName || drug.pillPackName || "",
            });
          }
        }
      }

      const drugs = Array.from(drugMap.values());

      // Extract prescription options for dose change dropdown
      const prescriptions: PrescriptionOption[] = parsed
        .filter((rx: any) => rx.prescriptionId && rx.drug && rx.drug.asin)
        .map((rx: any) => {
          const fills = rx.fills || [];
          const latestFill = fills[fills.length - 1];
          return {
            prescriptionId: rx.prescriptionId,
            daysSupply: rx.daysSupply || 0,
            drugAsin: rx.drug.asin,
            orderId: latestFill?.orderId || "",
          };
        });

      if (drugs.length === 0) {
        this.setState({
          isLoadingPrescriptions: false,
          drugs: [],
          flashMessages: [{ type: "info", content: "No prescriptions found for this profile ID.", dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
        });
      } else {
        this.setState({ isLoadingPrescriptions: false, drugs, prescriptions });
      }
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to fetch prescriptions";
      this.setState({
        isLoadingPrescriptions: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    }
  };

  private handleDrugChange = (drug: DrugOption | null) => {
    this.setState({
      selectedDrug: drug,
      overrides: [],
      formData: EMPTY_FORM_DATA,
      approvalResponse: null,
      overrideApprovalCompleted: false,
      flashMessages: [],
    });
    if (drug) {
      this.fetchOverrides(drug.asin);
    }
  };

  private fetchOverrides = async (drugAsin: string) => {
    const { profileId } = this.state;
    this.setState({ isLoadingOverrides: true });

    try {
      const response = await API.post("ApaqPortalAPI", "/override/get", {
        body: { healthProfileId: profileId, drugAsin },
      });
      this.setState({ overrides: response.overrides || [], isLoadingOverrides: false });
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to fetch overrides";
      this.setState({
        isLoadingOverrides: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    }
  };

  private handleSubmit = async () => {
    if (this.state.isSubmitting) return;

    const { profileId, selectedDrug, formData } = this.state;
    if (!selectedDrug) return;

    this.setState({ isSubmitting: true, flashMessages: [], approvalResponse: null, overrideApprovalCompleted: false });

    try {
      // If skip approval service is toggled, go directly to create UCS override
      if (formData.skipApprovalService) {
        await this.createUcsOverride("");
        return;
      }

      // Step 1: Submit approval
      const approvalRequest: any = {
        healthProfileId: profileId,
        drugAsin: selectedDrug.asin,
        reason: formData.reason,
        requesterRole: formData.requesterRole,
      };
      if (formData.notes) approvalRequest.notes = formData.notes;
      if (formData.includeControlledSubstanceDetails && formData.controlledSubstanceDetails) {
        approvalRequest.controlledSubstanceDetails = {
          ...formData.controlledSubstanceDetails,
          approvedAt: formData.controlledSubstanceDetails.approvedAt ? `${formData.controlledSubstanceDetails.approvedAt}T00:00:00Z` : "",
        };
      }
      if (formData.vacationDetails) {
        approvalRequest.vacationDetails = {
          ...formData.vacationDetails,
          travelStartAt: formData.vacationDetails.travelStartAt ? `${formData.vacationDetails.travelStartAt}T00:00:00Z` : "",
          travelEndAt: formData.vacationDetails.travelEndAt ? `${formData.vacationDetails.travelEndAt}T00:00:00Z` : "",
        };
      }

      const approvalResponse = await API.post("ApaqPortalAPI", "/approval/submit", { body: approvalRequest });

      const status = approvalResponse.status;
      const approvalId = approvalResponse.approvalId;

      this.setState({ approvalResponse }, () => {
        // Only scroll to approval response when user needs to take manual action
        if (status === ApprovalStatus.REQUIRES_AUTHORIZED_HUMAN_REVIEW && !formData.autoOverrideApproval) {
          setTimeout(() => {
            this.approvalResponseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }
      });

      if (status === ApprovalStatus.GRANTED && approvalId) {
        // Proceed directly to create UCS override (isSubmitting stays true)
        await this.createUcsOverride(approvalId);
      } else if (status === ApprovalStatus.REQUIRES_AUTHORIZED_HUMAN_REVIEW && formData.autoOverrideApproval && approvalId) {
        // Auto-override with PHARMACIST role then create UCS override (isSubmitting stays true)
        await this.overrideApprovalAndCreateOverride(approvalId, RequesterRole.PHARMACIST);
      } else if (status === ApprovalStatus.DENIED) {
        this.setState({
          isSubmitting: false,
          flashMessages: [{
            type: "error",
            content: `Approval denied. Reasons: ${(approvalResponse.rejectionReasons || []).join(", ")}`,
            dismissible: true,
            onDismiss: () => this.setState({ flashMessages: [] }),
          }],
        }, this.scrollToFlashbar);
      } else {
        // REQUIRES_HUMAN_REVIEW with auto off — stop submitting, user decides
        this.setState({ isSubmitting: false });
      }
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to submit approval";
      this.setState({
        isSubmitting: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    }
  };

  private handleOverrideApproval = async (role: string) => {
    const { approvalResponse } = this.state;
    if (!approvalResponse) return;

    this.setState({ isOverridingApproval: true });
    await this.overrideApprovalAndCreateOverride(approvalResponse.approvalId, role);
  };

  private overrideApprovalAndCreateOverride = async (approvalId: string, role?: string) => {
    try {
      // Call override approval — default to PHARMACIST for auto-override
      await API.post("ApaqPortalAPI", "/approval/override", {
        body: {
          approvalId,
          requesterRole: role || RequesterRole.PHARMACIST,
        },
      });

      this.setState({ overrideApprovalCompleted: true, isOverridingApproval: false });

      // Now create UCS override
      await this.createUcsOverride(approvalId);
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to override approval";
      this.setState({
        isOverridingApproval: false,
        isSubmitting: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      }, this.scrollToFlashbar);
    }
  };

  private createUcsOverride = async (approvalId: string) => {
    const { profileId, selectedDrug, formData } = this.state;
    if (!selectedDrug) return;

    this.setState({ isCreatingOverride: true });

    try {
      // Map approval reason to UCS override reason
      const overrideReason = APPROVAL_TO_OVERRIDE_REASON[formData.reason as ApprovalReason] || formData.reason;

      const overrideRequest: any = {
        healthProfileId: profileId,
        drugAsin: selectedDrug.asin,
        overrideReason,
      };
      if (approvalId) {
        overrideRequest.approvalId = approvalId;
      }

      // Add override details if present
      const details = formData.overrideDetails;
      if (details.prescriptionId || details.replacedOrderId || details.actualDepletionDate) {
        overrideRequest.overrideDetails = {};
        if (details.prescriptionId) overrideRequest.overrideDetails.prescriptionId = details.prescriptionId;
        if (details.replacedOrderId) overrideRequest.overrideDetails.replacedOrderId = details.replacedOrderId;
        if (details.actualDepletionDate) overrideRequest.overrideDetails.actualDepletionDate = details.actualDepletionDate;
      }

      await API.post("ApaqPortalAPI", "/override/create", { body: overrideRequest });

      this.setState({
        flashMessages: [{ type: "success", content: "Override created successfully.", dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
        formData: EMPTY_FORM_DATA,
        isCreatingOverride: false,
        isSubmitting: false,
      }, this.scrollToFlashbar);

      // Refresh overrides table
      this.fetchOverrides(selectedDrug.asin);
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to create UCS override";
      this.setState({
        isCreatingOverride: false,
        isSubmitting: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      }, this.scrollToFlashbar);
    }
  };

  render() {
    const { navOpen, profileId, isLoadingPrescriptions, drugs, selectedDrug, overrides, isLoadingOverrides, formData, isSubmitting, approvalResponse, isOverridingApproval, overrideApprovalCompleted, isCreatingOverride, flashMessages, activePage } = this.state;

    return (
      <PageShell
        navigationOpen={navOpen}
        onNavigationChange={(open) => this.setState({ navOpen: open })}
        navigation={
          <SideNavigation
            header={{
              text: "Consumption Guard",
              href: "#create-override",
            }}
            activeHref={`#${activePage}`}
            onFollow={e => {
              e.preventDefault();
              this.setState({ activePage: e.detail.href.replace("#", "") as PageView, flashMessages: [] });
            }}
            items={[
              {
                type: "section",
                text: "Consumption Guard",
                items: [
                  { type: "link", text: "Create Override History", href: "#create-override" },
                  { type: "link", text: "Expire Overrides", href: "#expire-overrides" },
                  { type: "link", text: "Place Order", href: "#place-order" },
                  { type: "link", text: "Consumption Date Estimates", href: "#consumption-estimates" },
                ],
              },
            ]}
          />
        }
        content={
          <div>
            <Box padding={{ top: "l", bottom: "l" }}>
              <Header variant="h1">Consumption Guard</Header>
            </Box>
            <SpaceBetween size="l">
              {flashMessages.length > 0 && (
                <div ref={this.flashbarRef}>
                  <Flashbar items={flashMessages} />
                </div>
              )}

              <ProfileLookupSection
                profileId={profileId}
                onChange={(value) => this.setState({ profileId: value })}
                onSubmit={this.handleLookup}
                isLoading={isLoadingPrescriptions}
              />

              {drugs.length > 0 && (
                <DrugSelector
                  drugs={drugs}
                  selectedDrug={selectedDrug}
                  onChange={this.handleDrugChange}
                />
              )}

              {/* Overrides Table — visible on all views when drug selected */}
              {selectedDrug && (
                <OverridesTable
                  overrides={overrides}
                  isLoading={isLoadingOverrides}
                  onRefresh={() => this.fetchOverrides(selectedDrug.asin)}
                  drugAsin={selectedDrug.asin}
                />
              )}

              {/* Create Override View */}
              {activePage === "create-override" && selectedDrug && (
                <CreateOverrideForm
                  formData={formData}
                  onChange={(updated) => this.setState({ formData: updated })}
                  onSubmit={this.handleSubmit}
                  isSubmitting={isSubmitting}
                  prescriptions={this.state.prescriptions}
                  selectedDrugAsin={selectedDrug.asin}
                />
              )}

              {activePage === "create-override" && approvalResponse && (
                <div ref={this.approvalResponseRef}>
                  <ApprovalResponseSection
                    response={approvalResponse}
                    onOverrideApproval={this.handleOverrideApproval}
                    isOverriding={isOverridingApproval}
                    overrideCompleted={overrideApprovalCompleted}
                  />
                </div>
              )}

              {activePage === "create-override" && isCreatingOverride && (
                <Box textAlign="center" padding="l">
                  <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <Spinner size="normal" />
                    <Box>Creating override...</Box>
                  </SpaceBetween>
                </Box>
              )}

              {/* Expire Overrides View */}
              {activePage === "expire-overrides" && selectedDrug && (
                <ExpireOverridesSection
                  key={selectedDrug.asin}
                  profileId={profileId}
                  drugAsin={selectedDrug.asin}
                  onExpireSuccess={() => this.fetchOverrides(selectedDrug.asin)}
                />
              )}

              {activePage === "expire-overrides" && !selectedDrug && drugs.length > 0 && (
                <Box color="text-body-secondary" padding="l">Select a drug above to expire overrides.</Box>
              )}

              {/* Place Order View */}
              {activePage === "place-order" && selectedDrug && (
                <PlaceOrderSection
                  key={`${profileId}-${selectedDrug.asin}`}
                  profileId={profileId}
                  prescriptions={this.state.prescriptions}
                  selectedDrugAsin={selectedDrug.asin}
                  onOrderSuccess={() => this.fetchOverrides(selectedDrug.asin)}
                />
              )}

              {activePage === "place-order" && !selectedDrug && drugs.length > 0 && (
                <Box color="text-body-secondary" padding="l">Select a drug above to place an order.</Box>
              )}

              {/* Consumption Estimates View */}
              {activePage === "consumption-estimates" && selectedDrug && (
                <ConsumptionEstimatesSection
                  key={selectedDrug.asin}
                  profileId={profileId}
                  drugAsin={selectedDrug.asin}
                />
              )}

              {activePage === "consumption-estimates" && !selectedDrug && drugs.length > 0 && (
                <Box color="text-body-secondary" padding="l">Select a drug above to get consumption based date estimates.</Box>
              )}
            </SpaceBetween>
          </div>
        }
      />
    );
  }
}

export default ConsumptionGuardOverridePage;
